import { encodeAffinityParam } from '../../utils/affinity-param';
import { getAnonymousId } from '../../utils/tracking-context';
import { fetchPastPageShim } from '../../utils/native-fetch';
import { rememberRankingVariant } from '../../utils/ranking-variant-store';
import {
    LS_Set,
    LS_Get,
    LS_Delete,
    Cookies,
} from "../../utils/local-storage";

import { ObjectToArray, createNotification } from "../../utils/dom";
import {
    LISTING_SEARCH_EMPTY
} from "../js/server-constants"
import { getListingImageUrl } from "../../utils/listing-image-url";
import { GetListingResponseSchema } from "@/schemas/api/listings";
import {
    SearchListingsResponseSchema,
    HomeShowcasesResponseSchema,
} from "@/schemas/api/search";
import { ShowcaseResponseSchema } from "@/schemas/api/search/showcase";
import { SpecialShowcasesSuccessResponseSchema } from "@/schemas/api/recommendations/special-showcases";
import { TagFallbackResponseSchema } from "@/schemas/api/search/tag-fallback";
import { SearchTagShowcasesResponseSchema } from "@/schemas/api/search/tag-showcases";
import { SearchFacetsResponseSchema, type SearchFacets } from "@/schemas/api/search/facets";
import { filtersToQuery, type FilterState } from "../../utils/search-filter-state";
import { trackError, ErrorSeverity } from "../../utils/error-tracking";
import { getBaseUrl } from "../../utils/base-url";
import type { SearchListing, SearchListingUser } from "@/schemas/api/search/listings";
import type { GetListingData } from "@/schemas/api/listings/get";
import { appendPageLoadAt, appendShuffleSeed, getPageLoadAt, getPageLoadShuffleSeed } from "@/utils/seeded-shuffle";
import { ShopSearchSuccessResponseSchema, type MatchedTag, type ShopExactTag } from "@/schemas/api/search/shop";

const MAX_LISTING_PER_PAGE = 32

/**
 * Largest page size a client may ask for. Mirrors MAX_SEARCH_LIMIT in
 * utils/search-filters.ts, which enforces it again server-side — this copy just
 * keeps an obviously-wrong number from leaving the browser.
 */
export const MAX_SEARCH_PAGE_SIZE = 200;

/**
 * Elasticsearch will not serve a hit past index.max_result_window, so page N of
 * size S only exists while N * S <= 10000. Mirrors ES_MAX_RESULT_WINDOW in
 * utils/elasticsearch/query-builders.ts, duplicated rather than imported so a
 * client bundle does not pull in the server query builders.
 */
export const ES_MAX_RESULT_WINDOW = 10000;

/** The highest page number that is actually reachable at a given page size. */
export function maxReachablePage(pageSize: number): number {
    const size = Math.max(1, pageSize);
    return Math.max(1, Math.floor(ES_MAX_RESULT_WINDOW / size));
}

export interface SearchPageResult {
    /** Rows in the same shape `fetchSearchList` returns, validated by the same schema. */
    listings: SearchListing[];
    /** Total matching listings. Capped at 10,000 unless the query names a userId. */
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
}

interface SearchParams {
    categoryId?: number | number[]; // Specific category/subcategory ID
    categoryGroupId?: number; // Category group ID
    q?: string;
    min?: number;
    max?: number;
    minPrice?: number;
    maxPrice?: number;
    itemCondition?: number[] | null;
    page?: number;
    offset?: number; // Start row; overrides `page` (a row whose first items came from another route)
    userId?: string | number | null;
    excludeUserId?: number;
    excludeListingId?: number;
    isHomePage?: boolean; // If true, use home-listings endpoint with category exclusions
    status?: number | number[]; // Listing status: 1=Active, 4=Pending, 5=Sold
    sold?: boolean | number | string; // Include sold items in search results
    shippable?: boolean | number | string; // Filter to only shippable items
    tags?: string[]; // Filter to listings carrying these tags (keeps tag-seeded rows on-topic)
    sellerCap?: boolean; // false: no per-seller step-down (a Shop tag page, utils/seller-diversity.ts)
    limit?: number; // Page size. Clamped to MAX_SEARCH_PAGE_SIZE before it reaches the server.
    sortBy?: string; // ES sort key ('date_desc', 'price_asc', ...). Omitted means relevance order.
    near?: boolean; // The buyer's local row: the server reads their IP position (utils/near-you.ts).
    featured?: boolean | number | string; // Featured listings only
    deals?: boolean; // Deals Only (utils/deal-quality-filter.ts)
    quality?: boolean; // Quality Only (utils/deal-quality-filter.ts)
    // Listing attribute filters (utils/search-filter-state.ts): each matches any of its values.
    gender?: string[];
    ageGroup?: string[];
    size?: string[];
    color?: string[];
    brand?: string[];
    material?: string[];
    tf?: string; // Tag and type filters (utils/tag-facet-param.ts)
}

/**
 * The one request both `fetchSearchList` and `fetchSearchPage` make.
 *
 * They differ only in what they hand back: the bare array that every existing
 * caller expects, or the array plus the paging metadata the endpoint has always
 * returned and this module used to discard.
 */
async function requestSearchPage({
    categoryId,
    categoryGroupId,
    q = "",
    min = 0,
    max = 0,
    minPrice,
    maxPrice,
    itemCondition = null,
    page = 1,
    offset,
    userId = null,
    excludeUserId,
    excludeListingId,
    isHomePage = false,
    status,
    sold,
    shippable,
    tags,
    sellerCap,
    limit,
    sortBy,
    near,
    featured,
    deals,
    quality,
    gender,
    ageGroup,
    size,
    color,
    brand,
    material,
    tf,
}: SearchParams = {}): Promise<SearchPageResult> {
    // Page size is clamped here as well as on the server: a value out of a URL
    // or a stale localStorage entry should never become the number of documents
    // Elasticsearch is asked to assemble.
    const requestedLimit = Number(limit);
    const safeLimit = Number.isFinite(requestedLimit) && requestedLimit > 0
        ? Math.min(Math.floor(requestedLimit), MAX_SEARCH_PAGE_SIZE)
        : MAX_LISTING_PER_PAGE;
    const safePage = Number.isFinite(Number(page)) && Number(page) > 0 ? Math.floor(Number(page)) : 1;
    const empty: SearchPageResult = { listings: [], total: 0, page: safePage, limit: safeLimit, hasMore: false };
    // Match prod implementation - simpler price handling
    const finalMinPrice = minPrice !== undefined ? Number(minPrice) : Number(min);
    const finalMaxPrice = maxPrice !== undefined ? Number(maxPrice) : Number(max);

    // Build query parameters for Elasticsearch endpoint
    const params = new URLSearchParams();

    // Text query
    if (q && q.trim().length > 0) {
        params.append('q', q.trim());
    }

    // Category filters
    const normalizedCategoryId = Array.isArray(categoryId) ? categoryId[0] : categoryId;
    if (normalizedCategoryId && normalizedCategoryId > 0) {
        params.append('categoryId', String(normalizedCategoryId));
    }
    if (categoryGroupId && categoryGroupId > 0) {
        params.append('categoryGroupId', String(categoryGroupId));
    }

    // Price range
    if (finalMinPrice > 0) {
        params.append('minPrice', String(finalMinPrice));
    }
    if (finalMaxPrice > 0) {
        params.append('maxPrice', String(finalMaxPrice));
    }

    // Item condition (supports array)
    if (itemCondition && Array.isArray(itemCondition) && itemCondition.length > 0) {
        params.append('itemCondition', itemCondition.join(','));
    }

    // User filter
    if (userId) {
        params.append('userId', String(userId));
    }

    // Exclude listing ID
    if (excludeListingId) {
        params.append('excludeListingId', String(excludeListingId));
    }

    // Exclude user ID
    if (excludeUserId) {
        params.append('excludeUserId', String(excludeUserId));
    }

    // Status filter - only pass if explicitly provided
    // Default status logic is now handled by the search API endpoint
    if (status !== undefined) {
        if (Array.isArray(status)) {
            params.append('status', status.join(','));
        } else {
            params.append('status', String(status));
        }
    }

    // Include sold items filter
    if (sold !== undefined && sold !== null) {
        params.append('sold', String(sold));
    }

    // Shippable only filter
    if (shippable !== undefined && shippable !== null) {
        params.append('shippable', String(shippable));
    }

    // Featured only. It was in the filter panel but never sent, so the toggle did nothing.
    if (featured === true || featured === 'true' || featured === 1 || featured === '1') {
        params.append('featured', 'true');
    }

    // Deals Only / Quality Only. Added 2026-10-11 to SearchProvider but not here,
    // so browse pages sent neither and showed every listing.
    if (deals) params.append('deals', 'true');
    if (quality) params.append('quality', 'true');

    // Attribute filters, each a comma list (sizes and brands never hold commas
    // in our vocabulary; a brand that did would split, and match nothing).
    const attributes = { gender, ageGroup, size, color, brand, material };
    for (const [key, values] of Object.entries(attributes)) {
        if (Array.isArray(values) && values.length > 0) params.append(key, values.join(','));
    }

    // Tag and type filters, one string (utils/tag-facet-param.ts).
    if (tf) params.append('tf', tf);

    // Tag filter — repeated params so multi-word tag values survive commas.
    // Keeps tag-seeded showcase rows paginating on their own tag.
    if (tags && tags.length > 0) {
        tags.forEach((t) => params.append('tags', t));
    }
    if (sellerCap === false) params.append('sellerCap', 'off');

    // The buyer's local row. The server reads the position from the request.
    if (near) {
        params.append('near', 'true');
    }

    // Explicit ordering. Left off unless a caller asks, so every existing caller
    // keeps the relevance order it has always had. A caller that pages deeply
    // needs it: with no sort clause the score is the organic recency boost, which
    // only applies to the last 30 days, so everything older ties and ES orders
    // the tie by internal doc id — an order that is neither meaningful nor stable
    // across two requests. "Page 12" only means something once the sort does.
    if (sortBy) {
        params.append('sortBy', sortBy);
    }

    // Pagination
    params.append('page', String(safePage));
    params.append('limit', String(safeLimit));
    if (offset !== undefined && Number.isFinite(offset) && offset >= 0) {
        params.append('offset', String(Math.floor(offset)));
    }
    // This page load's shuffle seed (utils/seeded-shuffle.ts). Browser only.
    appendShuffleSeed(params);
    // The page load's start, so "already seen" holds still across pages (utils/seen-listings.ts).
    // Home and browse/search both use it; both routes are no-store.
    appendPageLoadAt(params);

    try {
        // Use absolute URL for SSR/SSG context, relative URL for client-side
        const isServer = typeof window === 'undefined';
        const baseUrl = isServer ? getBaseUrl() : '';

        // Use home-listings endpoint for home page (excludes Pets, Misc, Jobs)
        const endpoint = isHomePage ? '/api/search/home-listings' : '/api/search/listings';
        const url = `${baseUrl}${endpoint}?${params.toString()}`;

        const response = await fetch(url);

        if (!response.ok) {
            return empty;
        }

        const rawData = await response.json();

        // Validate response with Zod schema
        const parseResult = SearchListingsResponseSchema.safeParse(rawData);
        if (!parseResult.success) {
            console.error('[fetchSearchList] Validation failed:', parseResult.error.flatten());
            return empty;
        }

        // Use rawData after validation passes - Zod strips listing fields not in schema
        const data = rawData;

        if (!data.success) {
            return empty;
        }

        const listings = data.listings || [];

        return {
            // Add BargainHunter photos if missing
            listings: listings.map(addBargainHunterPhotos),
            total: typeof data.total === 'number' ? data.total : listings.length,
            page: typeof data.page === 'number' ? data.page : safePage,
            limit: typeof data.limit === 'number' ? data.limit : safeLimit,
            hasMore: Boolean(data.hasMore),
        };
    } catch (error) {
        return empty;
    }
}

export async function fetchSearchList(params: SearchParams = {}): Promise<SearchListing[]> {
    const result = await requestSearchPage(params);
    return result.listings;
}

/**
 * Same request as `fetchSearchList`, keeping the paging metadata.
 *
 * A caller that only renders rows wants the array and nothing else, which is why
 * this is a second function rather than a wider return type on the first — a
 * seller paging through thousands of their own listings is the only caller that
 * needs to know how many there are.
 *
 * Fails closed exactly as `fetchSearchList` does: a bad status, a response the
 * schema rejects, or a thrown request all produce an empty page rather than a
 * rejection, so a caller never renders half-validated data.
 */
export async function fetchSearchPage(params: SearchParams = {}): Promise<SearchPageResult> {
    return requestSearchPage(params);
}

export interface TagFallbackParams {
    /** Explicit seed tags. If omitted, the server derives them from the context below. */
    seedTags?: string[];
    /** Tags on the items currently shown — used to derive seeds server-side. */
    currentListTags?: string[];
    /** Listing IDs to derive seeds from when no tags are available. */
    listingIds?: number[];
    /** Already-shown listing IDs to exclude from results. */
    excludeListingIds?: number[];
    /** Tags already searched on previous fallback pages (drift accumulator). */
    seenTags?: string[];
    limit?: number;
    categoryGroupId?: number;
}

export interface TagFallbackResult {
    listings: SearchListing[];
    seedTagsUsed: string[];
    seenTags: string[];
    hasMore: boolean;
}

/**
 * Caps on what a fallback page sends. A feed that has been scrolled for a while
 * holds thousands of ids and tags; sending all of them made a >64KB request line
 * that the edge rejects outright (measured: 32KB passes, 64KB is a 414, 78KB
 * never connects), and the caller's own dedup already covers anything past the
 * recent window. Excess exclusions also cost ES query time for nothing.
 */
const TAG_FALLBACK_MAX_EXCLUDE_IDS = 300;
const TAG_FALLBACK_MAX_SEED_IDS = 100;
const TAG_FALLBACK_MAX_TAGS = 40;
// The drift accumulator grows a few tags per page; the server only walks out
// from the trailing slice, so older entries just pad the request.
const TAG_FALLBACK_MAX_SEEN_TAGS = 300;

/**
 * Who turned a tag-fallback request away. The route answers only 200, 405 and
 * 500, yet rows keep arriving as a bare "Request failed: 403" from ordinary
 * phones on US IPs -- the Vercel firewall refuses API calls while the page HTML
 * still serves, and the row could not say which rule or whether a function
 * ran. Low-cardinality tokens only: x-vercel-id itself is per-request and would
 * split the cluster into one row each.
 */
function describeRefusal(response: Response): string {
    const header = (name: string) => response.headers?.get(name) ?? null;
    const contentType = header('content-type') ?? '';
    const body = contentType.includes('json')
        ? 'json'
        : contentType.includes('html')
          ? 'html'
          : contentType
            ? 'other'
            : 'none';
    // One region per hop: "iad1::<id>" was answered at the edge,
    // "iad1::iad1::<id>" reached a function.
    const vercelId = header('x-vercel-id');
    const answeredBy = vercelId ? (vercelId.split('::').length > 2 ? 'function' : 'edge') : 'unknown';
    return `mitigated=${header('x-vercel-mitigated') ?? 'none'} body=${body} answered-by=${answeredBy}`;
}

/**
 * A 401/403 whose body is not JSON. The tag-fallback route answers only in
 * JSON and never with 403, so this page was written by something in front of
 * it -- the Vercel firewall, or a network filter between the visitor and us
 * (rows with no x-vercel-id at all). A refused visitor, not an app fault.
 */
function isUpstreamRefusal(response: Response): boolean {
    if (response.status !== 401 && response.status !== 403) return false;
    return !(response.headers?.get('content-type') ?? '').includes('json');
}

const REFUSAL_TEXT_LIMIT = 80;

/** First text of a tag in an HTML page, whitespace-folded, IPv4s masked; 'none' when absent. */
function firstTagText(html: string, tag: string): string {
    const match = new RegExp(`<${tag}[^>]*>([^<]*)</${tag}>`, 'i').exec(html);
    const text = match?.[1]
        ?.replace(/\s+/g, ' ')
        .replace(/\b\d{1,3}(?:\.\d{1,3}){3}\b/g, '<ip>')
        .trim();
    return text ? text.slice(0, REFUSAL_TEXT_LIMIT) : 'none';
}

const REFUSAL_HOSTS_LIMIT = 3;

/**
 * Hosts the page loads or links to (src/href/action), ours left out; 'none'
 * when it names none. A refusal page with no title and no heading still
 * pulls its logo, stylesheet or "request access" form from the filter's own
 * domain -- 2026-10-21 rows (cluster 1bbc09c57aea9c65) named only a
 * `gunicorn` hop, on a second network after the 2026-10-17 one.
 */
function pageHosts(html: string): string {
    const hosts = new Set<string>();
    for (const match of html.matchAll(/\b(?:src|href|action)\s*=\s*["']?(?:https?:)?\/\/([a-z0-9.-]+)/gi)) {
        const host = (match[1] ?? '').toLowerCase().replace(/\.$/, '');
        if (!host || /(^|\.)sellwild\.com$/.test(host)) continue;
        hosts.add(/^\d{1,3}(?:\.\d{1,3}){3}$/.test(host) ? '<ip>' : host);
        if (hosts.size >= REFUSAL_HOSTS_LIMIT) break;
    }
    return hosts.size > 0 ? [...hosts].join(',') : 'none';
}

interface RefusalPage {
    pageTitle: string;
    pageHeading: string;
    pageHosts: string;
    pageBytes: number | null;
}

/**
 * What the refusal page says about who refused. The <title> usually names it
 * ("Access Denied", "Web Page Blocked", "Attention Required! | Cloudflare"),
 * but the 2026-10-12/13 rows -- one iPhone on one network, every tag-fallback
 * POST refused while its searches and analytics POSTs went through -- had no
 * title at all, so the first heading and the page size are kept as well. No
 * body text beyond those: these pages carry the visitor's IP and a ray id.
 */
async function readRefusalPage(response: Response): Promise<RefusalPage> {
    try {
        const text = await response.text();
        const h1 = firstTagText(text, 'h1');
        return {
            pageTitle: firstTagText(text, 'title'),
            pageHeading: h1 !== 'none' ? h1 : firstTagText(text, 'h2'),
            pageHosts: pageHosts(text),
            pageBytes: text.length,
        };
    } catch {
        return { pageTitle: 'unreadable', pageHeading: 'unreadable', pageHosts: 'unreadable', pageBytes: null };
    }
}

/**
 * The `server` and `via` headers name the proxy or filter that wrote a
 * refusal with no x-vercel-id ("nginx", "Fortinet", "1.1 squid"). Product
 * tokens only, so the cardinality stays low; 'none' when absent.
 */
function refusingHop(response: Response): { server: string; via: string } {
    const header = (name: string) =>
        (response.headers?.get(name) ?? '')
            .replace(/\b\d{1,3}(?:\.\d{1,3}){3}\b/g, '<ip>')
            .trim()
            .slice(0, REFUSAL_TEXT_LIMIT) || 'none';
    return { server: header('server'), via: header('via') };
}

/** Keep the most recent N entries — the newest items are the ones on screen. */
function tailCap<T>(values: T[] | undefined, max: number): T[] {
    if (!values || values.length === 0) return [];
    return values.length > max ? values.slice(-max) : values;
}

/**
 * Fetch tag-based fallback listings ("recommend forever"). Returns listings in
 * the same shape as fetchSearchList plus the drift metadata (seedTagsUsed,
 * seenTags, hasMore) the caller threads back in on the next page.
 */
export async function fetchTagFallbackList(params: TagFallbackParams = {}): Promise<TagFallbackResult> {
    const empty: TagFallbackResult = { listings: [], seedTagsUsed: [], seenTags: params.seenTags || [], hasMore: false };
    try {
        const isServer = typeof window === 'undefined';
        const baseUrl = isServer ? getBaseUrl() : '';

        // POSTed as a JSON body rather than a query string: these arrays are
        // unbounded in the caller (every listing and tag on screen), and a GET
        // long enough to carry them is refused by the edge — which this
        // function then swallowed as "no more results", silently ending the feed.
        const body = {
            seedTags: tailCap(params.seedTags, TAG_FALLBACK_MAX_TAGS),
            currentListTags: tailCap(params.currentListTags, TAG_FALLBACK_MAX_TAGS),
            seenTags: tailCap(params.seenTags, TAG_FALLBACK_MAX_SEEN_TAGS),
            excludeListingIds: tailCap(params.excludeListingIds, TAG_FALLBACK_MAX_EXCLUDE_IDS),
            listingIds: tailCap(params.listingIds, TAG_FALLBACK_MAX_SEED_IDS),
            limit: params.limit ?? 24,
            ...(params.categoryGroupId && params.categoryGroupId > 0
                ? { categoryGroupId: params.categoryGroupId }
                : {}),
            // This page load's shuffle seed (utils/seeded-shuffle.ts). Browser only.
            ...(getPageLoadShuffleSeed() !== undefined ? { seed: getPageLoadShuffleSeed() } : {}),
            // The page load's start, for "already seen" (utils/seen-listings.ts).
            ...(getPageLoadAt() !== undefined ? { at: getPageLoadAt() } : {}),
        };

        const url = `${baseUrl}/api/search/tag-fallback`;
        const requestBody = JSON.stringify(body);
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: requestBody,
        });
        if (!response.ok) {
            if (isUpstreamRefusal(response)) {
                // Status only in the message; the rest goes in extra so the
                // title cannot split the cluster. The request's size and
                // contents count say whether a filter refuses every POST here
                // or only the large ones.
                trackError(`[fetchTagFallbackList] Request refused: HTTP ${response.status}`, ErrorSeverity.WARNING, {
                    source: 'fetchTagFallbackList',
                    action: 'tag-fallback-refused',
                    extra: {
                        status: response.status,
                        refusal: describeRefusal(response),
                        ...refusingHop(response),
                        ...(await readRefusalPage(response)),
                        requestBytes: requestBody.length,
                        requestTags: body.currentListTags.length + body.seenTags.length,
                        requestIds: body.excludeListingIds.length + body.listingIds.length,
                    },
                });
                return empty;
            }
            console.error('[fetchTagFallbackList] Request failed:', response.status, describeRefusal(response));
            return empty;
        }

        const rawData = await response.json();
        const parseResult = TagFallbackResponseSchema.safeParse(rawData);
        if (!parseResult.success) {
            console.error('[fetchTagFallbackList] Validation failed:', parseResult.error.flatten());
            return empty;
        }
        if (!parseResult.data.success) return empty;

        const data = parseResult.data;
        return {
            listings: (data.listings || []).map(addBargainHunterPhotos),
            seedTagsUsed: data.seedTagsUsed || [],
            seenTags: data.seenTags || [],
            hasMore: data.hasMore || false,
        };
    } catch (error) {
        return empty;
    }
}

export interface TagShowcasesParams {
    /** Scope to one category group's tags; omit on the home view (all groups by popularity). */
    categoryGroupId?: number;
    /** Tags already rendered on previous pages, so the next page drifts onward. */
    seenTags?: string[];
    /** Already-shown listing ids to exclude from the new rows. */
    seenListingIds?: number[];
    /** Number of showcase rows to return this page. */
    limit?: number;
    listingsPerTag?: number;
    minListingsPerTag?: number;
}

export interface TagShowcaseRowResult {
    tag: string;
    title?: string;
    viewCount: number;
    /** The group the row was held to; pass it on when paging the row. */
    categoryGroupId?: number;
    /** A brand row's indexed brand: page and link it by `brand`, not by `tag`. */
    brand?: string;
    /**
     * Not `SearchListing[]`: TagShowcaseSchema parses these as `z.array(z.any())`,
     * so unlike every other listings array in this file they have not actually
     * been checked against the row schema. Claiming the stronger type here would
     * describe a guarantee the validation does not make.
     */
    listings: Record<string, unknown>[];
}

export interface TagShowcasesResult {
    showcases: TagShowcaseRowResult[];
    hasMore: boolean;
    totalAvailable: number;
}

export interface NearYouShowcase {
    title: string;
    listings: SearchListing[];
}

/** What /shop/[keywords] needs before it can search: the phrase and the tags it matched. */
export interface ShopTags {
    searchPhrase: string;
    matchedTags: MatchedTag[];
    exactTag?: ShopExactTag;
}

// Only the fields the page reads: a change to the rest of the response
// must not blank the page (a client .parse() turns deploy skew into an outage).
const ShopTagsResponseSchema = ShopSearchSuccessResponseSchema.pick({ success: true, searchPhrase: true, matchedTags: true });
// Read on its own: a bad or missing exactTag costs the heading its plural, never the page.
const ExactTagFieldSchema = ShopSearchSuccessResponseSchema.pick({ exactTag: true });

/**
 * The tags a /shop keyword phrase matches. The page then runs the ordinary
 * ranked search for any of them in the browser, like /search. Null when the
 * request fails, so the page can tell "no match" from "could not ask".
 */
export async function fetchShopTags(keywords: string): Promise<ShopTags | null> {
    try {
        // One listing per tag keeps the call cheap: only the tags are used.
        const params = new URLSearchParams({ keywords, maxTags: '8', listingsPerTag: '1' });
        const response = await fetch(`/api/search/shop?${params.toString()}`);
        if (!response.ok) return null;
        const body: unknown = await response.json();
        const parsed = ShopTagsResponseSchema.safeParse(body);
        if (!parsed.success) return null;
        const exact = ExactTagFieldSchema.safeParse(body);
        const exactTag = exact.success ? exact.data.exactTag : undefined;
        return { searchPhrase: parsed.data.searchPhrase, matchedTags: parsed.data.matchedTags, ...(exactTag ? { exactTag } : {}) };
    } catch (error) {
        trackError(error instanceof Error ? error : String(error), ErrorSeverity.WARNING, { source: 'fetchShopTags', action: 'fetch' });
        return null;
    }
}

/**
 * The buyer's local row for the home page: its heading names their city and its
 * first listings come from the same search the For You row runs. Null when the
 * server cannot tell where the buyer is, or anything fails -- the row is simply
 * not drawn. Later pages go through `fetchSearchList({ near: true })`.
 */
export async function fetchNearYouShowcase(limit = 12): Promise<NearYouShowcase | null> {
    try {
        const qs = new URLSearchParams({ type: 'session', showcaseTypes: 'near_you', limit: String(limit) });
        const response = await fetch(`/api/recommendations/special-showcases?${appendShuffleSeed(qs)}`);
        if (!response.ok) return null;
        const parsed = SpecialShowcasesSuccessResponseSchema.safeParse(await response.json());
        if (!parsed.success) return null;
        const row = parsed.data.showcases.find(s => s.type === 'near_you');
        if (!row || row.listings.length === 0) return null;
        return { title: row.title, listings: row.listings as SearchListing[] };
    } catch (error) {
        trackError(error, ErrorSeverity.WARNING, { source: 'fetchNearYouShowcase', action: 'fetch' });
        return null;
    }
}

/**
 * Fetch the next page of category-group-seeded tag showcase rows ("keep filling
 * after the official categories"). Each row is one tag with its listings; the
 * caller threads `seenTags`/`seenListingIds` back in for the next page.
 */
export async function fetchTagShowcases(params: TagShowcasesParams = {}): Promise<TagShowcasesResult> {
    const empty: TagShowcasesResult = { showcases: [], hasMore: false, totalAvailable: 0 };
    try {
        const isServer = typeof window === 'undefined';
        const baseUrl = isServer ? getBaseUrl() : '';

        const qs = new URLSearchParams();
        if (params.categoryGroupId && params.categoryGroupId > 0) {
            qs.append('categoryGroupId', String(params.categoryGroupId));
        }
        // Repeated params (not comma-joined) so tag values containing commas survive.
        (params.seenTags || []).forEach(t => qs.append('seenTags', t));
        if (params.seenListingIds && params.seenListingIds.length > 0) {
            qs.append('seenListingIds', params.seenListingIds.join(','));
        }
        if (params.limit != null) qs.append('limit', String(params.limit));
        if (params.listingsPerTag != null) qs.append('listingsPerTag', String(params.listingsPerTag));
        if (params.minListingsPerTag != null) qs.append('minListingsPerTag', String(params.minListingsPerTag));

        const url = `${baseUrl}/api/search/tag-showcases?${appendShuffleSeed(qs)}`;
        const response = await fetch(url);
        if (!response.ok) return empty;

        const rawData = await response.json();
        const parseResult = SearchTagShowcasesResponseSchema.safeParse(rawData);
        if (!parseResult.success) {
            console.error('[fetchTagShowcases] Validation failed:', parseResult.error.flatten());
            return empty;
        }
        if (!parseResult.data.success) return empty;

        const data = parseResult.data;
        return {
            showcases: (data.showcases || []).map((s) => ({
                tag: s.tag,
                title: s.title,
                viewCount: s.viewCount,
                listings: (s.listings || []).map(addBargainHunterPhotos),
                categoryGroupId: s.categoryGroupId,
                ...(s.brand && { brand: s.brand }),
            })),
            hasMore: data.hasMore,
            totalAvailable: data.totalAvailable,
        };
    } catch (error) {
        return empty;
    }
}

export interface FetchSearchListItemOptions {
    /** Use 'db' to bypass Elasticsearch and fetch directly from database */
    source?: 'es' | 'db';
    /**
     * Called when the API answers 404: the listing is gone (sold, archived or
     * deleted), as opposed to a fetch that failed. Both return null, so a caller
     * that has to tell them apart needs this.
     */
    onNotFound?: () => void;
    /**
     * Called with the reason when the fetch returned null for anything other
     * than a 404. Most of these paths log nothing, so without this a caller
     * that bounces the reader can only say "it did not load".
     */
    onFailure?: (failure: ListingFetchFailure) => void;
}

/** Why fetchSearchListItem returned null when the API did not say 404. */
export interface ListingFetchFailure {
    reason: 'http-error' | 'invalid-response' | 'unsuccessful' | 'threw';
    detail: string;
}

/**
 * True when `id` is something we can actually ask the listings API for.
 *
 * Callers reach this with a router param that never resolved, which stringifies
 * to the literal "undefined"/"null" and produces a request for
 * `/api/listings/undefined`. That 4xx is the caller's bug, not the API's, so
 * catch it here and say whose id it was.
 */
function isFetchableListingId(id: unknown): id is string | number {
    if (typeof id === 'number') return Number.isFinite(id);
    if (typeof id !== 'string') return false;
    const trimmed = id.trim();
    return trimmed !== '' && trimmed !== 'undefined' && trimmed !== 'null';
}

export async function fetchSearchListItem(id: string, options: FetchSearchListItemOptions = {}): Promise<GetListingData | null> {
    if (!isFetchableListingId(id)) {
        trackError('[fetchSearchListItem] refusing to fetch an unusable listing id', ErrorSeverity.WARNING, {
            source: 'fetchSearchListItem',
            action: 'invalid-listing-id',
            extra: { rawId: String(id), rawIdType: typeof id },
        });
        createNotification({type: "error", msg: "Listing Not Found"});
        return null;
    }

    try {
        // Use new Elasticsearch API endpoint for individual listing
        const isServer = typeof window === 'undefined';
        const baseUrl = isServer ? getBaseUrl() : '';

        // Build URL with optional source parameter
        const params = new URLSearchParams();
        if (options.source) {
            params.append('source', options.source);
        }
        const queryString = params.toString();
        const url = `${baseUrl}/api/listings/${id}${queryString ? `?${queryString}` : ''}`;

        // A partner WebView's injected fetch wrapper can throw before the
        // request leaves the page; fetchPastPageShim asks again past it.
        const response = await fetchPastPageShim(url);

        if (!response.ok) {
            if (response.status === 404) {
                createNotification({type: "error", msg: "Listing Not Found"});
                options.onNotFound?.();
                return null;
            }
            // statusText is empty over HTTP/2, so the status code carries the
            // signal. Keep the id out of the message: fingerprints hash the
            // message, and interpolating an id splits one bug across every row.
            trackError(`[fetchSearchListItem] API error: HTTP ${response.status}`, ErrorSeverity.WARNING, {
                source: 'fetchSearchListItem',
                action: 'listing-fetch-failed',
                extra: {
                    status: response.status,
                    statusText: response.statusText,
                    listingId: String(id),
                    listingSource: options.source ?? 'es',
                },
            });
            options.onFailure?.({ reason: 'http-error', detail: `HTTP ${response.status}` });
            return null;
        }

        const rawData = await response.json();

        // Validate response with Zod schema
        const parseResult = GetListingResponseSchema.safeParse(rawData);
        if (!parseResult.success) {
            console.error('[fetchSearchListItem] Validation failed:', parseResult.error.flatten());
            createNotification({type: "error", msg: "Listing Not Found"});
            const issue = parseResult.error.issues[0];
            options.onFailure?.({
                reason: 'invalid-response',
                detail: issue ? `${issue.path.join('.')}: ${issue.message}`.slice(0, 200) : '',
            });
            return null;
        }

        // Use rawData after validation passes - Zod strips listing fields not in schema
        const data = rawData;

        if (!data.success || !data.listing) {
            createNotification({type: "error", msg: "Listing Not Found"});
            options.onFailure?.({ reason: 'unsuccessful', detail: data.success ? 'no listing' : 'success=false' });
            return null;
        }

        const listing = data.listing;

        // remote_url is already indexed in ES from LISTINGS_METADATA during indexing
        // No need to fetch it at runtime on staging

        return addBargainHunterPhotos(listing);
    } catch (error) {
        // Network errors (Load failed, timeout, etc.) are expected - don't pollute error dashboard
        // User already gets "Listing Not Found" notification
        createNotification({type: "error", msg: "Listing Not Found"});
        options.onFailure?.({
            reason: 'threw',
            detail: error instanceof Error ? `${error.name}: ${error.message}`.slice(0, 200) : String(error).slice(0, 200),
        });
        return null;
    }
}

// Helper to get subdirectory path for S3 photos (matches PHP getSubdir)
const getSubdir = (id: number): string => {
    const FILES_PER_DIR = 2000;
    if (id < FILES_PER_DIR) {
        return id.toString();
    }
    const remainder = id % FILES_PER_DIR;
    return getSubdir(Math.floor(id / FILES_PER_DIR)) + '/' + remainder;
};

// Construct S3 photo URL for BargainHunter listings
// Use auto-detection to get correct bucket (dev S3 or production CloudFront)
const constructBargainHunterPhotoUrl = (listingId: number, size?: string): string => {
    const sizeMap: { [key: string]: 'original' | 'thumb' | '320x320' } = {
        't': 'thumb',
        '320x320': '320x320'
    };
    const mappedSize = size ? sizeMap[size] || 'original' : 'original';
    return getListingImageUrl(listingId, { size: mappedSize, env: 'auto' });
};

// Add photos to BargainHunter listings if missing
// Note: Most external listings (GCC, Petfinder, etc.) should have photos from LISTINGS_METADATA via the API
// This is a fallback for BargainHunter which may need S3 URL construction
const addBargainHunterPhotos = <
    T extends { id?: string | number; dataSourceId?: number | null; photos?: unknown[] }
>(listing: T): T => {
    if (!listing) return listing;

    // Skip if photos already exist
    if (listing.photos && listing.photos.length > 0) {
        return listing;
    }

    // BargainHunter listings (dataSourceId 33) - construct S3 URLs
    // Use == for comparison to handle both string and number (schema coerces to number)
    if (listing.dataSourceId == 33) {
        const listingId = parseInt(String(listing.id));
        listing.photos = [{
            url: constructBargainHunterPhotoUrl(listingId), // Full resolution
            thumbUrl: constructBargainHunterPhotoUrl(listingId, '320x320') // Thumbnail for listings
        }];
    }

    return listing;
};

export async function fetchOtherListings(
    userId: string | number,
    productId: string | null = null,
    page: number = 1,
    sold?: boolean | number | string
): Promise<{ listings: SearchListing[], user: Partial<SearchListingUser> }> {
    try {
        // Use new Elasticsearch search API with userId filter
        const isServer = typeof window === 'undefined';
        const baseUrl = isServer ? getBaseUrl() : '';

        // Build query parameters
        const params = new URLSearchParams();
        params.append('userId', String(userId));
        params.append('page', String(page));
        params.append('limit', String(MAX_LISTING_PER_PAGE));
        params.append('sortBy', 'date_desc');

        if (sold !== undefined && sold !== null) {
            params.append('sold', String(sold));
        }

        const url = `${baseUrl}/api/search/listings?${params.toString()}`;
        console.log('[fetchOtherListings] Fetching:', url);
        const response = await fetch(url, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            // The route answers only 200, 405 and 500, yet a 2026-10-28 row
            // read only "API error: Forbidden" (a headless Chrome on a seller
            // page). Say who refused it and whether the browser was automated,
            // so the next row tells the firewall apart from our own code.
            if (isUpstreamRefusal(response)) {
                trackError(`[fetchOtherListings] Request refused: HTTP ${response.status}`, ErrorSeverity.ERROR, {
                    source: 'fetchOtherListings',
                    action: 'other-listings-refused',
                    extra: {
                        status: response.status,
                        refusal: describeRefusal(response),
                        ...refusingHop(response),
                        ...(await readRefusalPage(response)),
                        automated: typeof navigator === 'undefined' ? null : navigator.webdriver === true,
                    },
                });
                return { listings: [], user: {} };
            }
            console.error('[fetchOtherListings] API error:', response.status, response.statusText, describeRefusal(response));
            return { listings: [], user: {} };
        }

        const rawData = await response.json();

        // Validate response with Zod schema
        const parseResult = SearchListingsResponseSchema.safeParse(rawData);
        if (!parseResult.success) {
            console.error('[fetchOtherListings] Validation failed:', parseResult.error.flatten());
            return { listings: [], user: {} };
        }

        // Use rawData after validation passes - Zod strips listing fields not in schema
        const data = rawData;

        if (!data.success) {
            console.error('[fetchOtherListings] Search returned unsuccessful response');
            return { listings: [], user: {} };
        }

        const listings = data.listings || [];

        // Filter out the current product if provided and add BargainHunter photos
        const filteredListings = listings
            .map(addBargainHunterPhotos)
            .filter((l: SearchListing) => l.id !== productId);

        // Extract user from first listing (all listings from same user)
        const user = filteredListings.length > 0 && filteredListings[0].user
            ? filteredListings[0].user
            : {};

        return { listings: filteredListings, user };
    } catch (error) {
        console.error('[fetchOtherListings] Error:', error);
        return { listings: [], user: {} };
    }
}

/**
 * Fetch showcases for the home page
 * Returns category GROUP showcases (not subcategories) with minimum products
 */
export async function fetchHomeShowcases({
    minProductsPerGroup = 7,
    categoryGroups = [12, 15, 23, 24, 25, 26, 34, 17, 35, 19, 20, 16], // Default top categories
    affinityTags,
    pinnedGroups,
    rankGroups,
    serveCount,
    interest,
    sortBy,
}: {
    minProductsPerGroup?: number;
    categoryGroups?: number[];
    /**
     * The visitor's in-session tag-affinity vector. When present, the server
     * orders each row by overlap with it (see utils/affinity-param.ts). Absent
     * or empty is exactly the unpersonalised page.
     */
    affinityTags?: Array<{ tag: string; weight: number }>;
    /** Groups the server deferred earlier and should now serve as-is. */
    pinnedGroups?: number[];
    /** First request: rank every group in `categoryGroups`, serve the top `serveCount`. */
    rankGroups?: boolean;
    serveCount?: number;
    /** This session's interest per group (utils/session-interest.ts). */
    interest?: Array<{ categoryGroupId: number; weight: number }>;
    /** A picked sort: orders each row's listings. */
    sortBy?: string;
} = {}): Promise<{
    showcases: Array<{
        categoryGroupId: number;
        categoryGroupName: string;
        categoryGroupSlug: string;
        listings: SearchListing[];
    }>;
    /** Groups held back for a "Show less"; ask again for them at the end. */
    deferredGroups?: number[];
    /** With `rankGroups`: every candidate, best first, and each one's score. */
    groupOrder?: number[];
    groupScores?: Record<string, number>;
}> {
    try {
        const isServer = typeof window === 'undefined';
        const baseUrl = isServer ? getBaseUrl() : '';

        const params = new URLSearchParams();
        params.append('minProductsPerGroup', String(minProductsPerGroup));
        params.append('categoryGroups', categoryGroups.join(','));
        const affinity = encodeAffinityParam(affinityTags);
        if (affinity) params.append('affinity', affinity);
        if (pinnedGroups && pinnedGroups.length > 0) {
            params.append('pinnedGroups', pinnedGroups.join(','));
        }
        if (sortBy) params.append('sortBy', sortBy);
        if (rankGroups) {
            params.append('rankGroups', '1');
            if (serveCount) params.append('serveCount', String(serveCount));
            const pairs = (interest ?? [])
                .filter(i => i.weight > 0)
                .slice(0, 40)
                .map(i => `${i.categoryGroupId}:${Number(i.weight.toFixed(3))}`);
            if (pairs.length > 0) params.append('interest', pairs.join(','));
        }
        // Picks the ranking-experiment bucket, and names whose "Not Interested"
        // answers apply when the cookie is missing. It is already on every
        // analytics event this page emits, so this adds no identifier the
        // request did not already carry.
        if (!isServer) {
            const anonymousId = getAnonymousId();
            if (anonymousId) params.append('anonymousId', anonymousId);
        }

        const url = `${baseUrl}/api/search/home-showcases?${appendPageLoadAt(appendShuffleSeed(params))}`;
        const response = await fetch(url);

        if (!response.ok) {
            return { showcases: [] };
        }

        const rawData = await response.json();

        // Validate response with Zod schema
        const parseResult = HomeShowcasesResponseSchema.safeParse(rawData);
        if (!parseResult.success) {
            console.error('[fetchHomeShowcases] Validation failed:', parseResult.error.flatten());
            return { showcases: [] };
        }

        // Use rawData after validation passes - Zod strips showcase fields not in schema
        const data = rawData;

        if (!data.success) {
            return { showcases: [] };
        }

        // The arm the server served. Remembered for the visit so every event
        // this page emits afterwards carries the same label.
        if (!isServer) rememberRankingVariant(data.rankingVariant);

        return {
            showcases: data.showcases || [],
            ...(data.deferredGroups?.length ? { deferredGroups: data.deferredGroups } : {}),
            ...(Array.isArray(data.groupOrder) ? { groupOrder: data.groupOrder, groupScores: data.groupScores ?? {} } : {}),
        };
    } catch (error) {
        console.error('[fetchHomeShowcases] Error:', error);
        return { showcases: [] };
    }
}

/**
 * Fetch listings organized for showcase view with minimum products per category
 * Returns full showcases (7+ products) and partial showcases (<7 products)
 */
export async function fetchShowcaseListings({
    categoryGroupId,
    minProductsPerCategory = 7,
    q = "",
    minPrice,
    maxPrice,
    itemCondition = null,
    categoryIds = null,
    sortBy,
}: {
    categoryGroupId: number;
    minProductsPerCategory?: number;
    q?: string;
    minPrice?: number;
    maxPrice?: number;
    itemCondition?: number[] | null;
    categoryIds?: number[] | null;
    /** A picked sort: orders each row's listings (utils/listing-sort.ts). */
    sortBy?: string;
}): Promise<{
    fullShowcases: Array<{ categoryId: number; categoryName: string; categorySlug: string; listings: SearchListing[] }>;
    partialShowcases: Array<{ categoryId: number; categoryName: string; categorySlug: string; listings: SearchListing[] }>;
    hasEnoughForShowcase: boolean;
}> {
    try {
        const isServer = typeof window === 'undefined';
        const baseUrl = isServer ? getBaseUrl() : '';

        // Build query parameters
        const params = new URLSearchParams();
        params.append('categoryGroupId', String(categoryGroupId));
        params.append('minProductsPerCategory', String(minProductsPerCategory));

        if (q && q.trim().length > 0) {
            params.append('q', q.trim());
        }

        if (minPrice !== undefined && minPrice > 0) {
            params.append('minPrice', String(minPrice));
        }

        if (maxPrice !== undefined && maxPrice > 0) {
            params.append('maxPrice', String(maxPrice));
        }

        if (itemCondition && Array.isArray(itemCondition) && itemCondition.length > 0) {
            params.append('itemCondition', itemCondition.join(','));
        }

        if (categoryIds && Array.isArray(categoryIds) && categoryIds.length > 0) {
            params.append('categories', categoryIds.join(','));
        }

        if (sortBy) params.append('sortBy', sortBy);

        const url = `${baseUrl}/api/search/showcase?${appendShuffleSeed(params)}`;
        const response = await fetch(url);

        if (!response.ok) {
            return { fullShowcases: [], partialShowcases: [], hasEnoughForShowcase: false };
        }

        const rawData = await response.json();

        // Validate response with Zod schema
        const parseResult = ShowcaseResponseSchema.safeParse(rawData);
        if (!parseResult.success) {
            console.error('[fetchShowcaseListings] Validation failed:', parseResult.error.flatten());
            return { fullShowcases: [], partialShowcases: [], hasEnoughForShowcase: false };
        }

        // Use rawData after validation passes - Zod strips showcase fields not in schema
        const data = rawData;

        if (!data.success) {
            return { fullShowcases: [], partialShowcases: [], hasEnoughForShowcase: false };
        }

        return {
            fullShowcases: data.fullShowcases || [],
            partialShowcases: data.partialShowcases || [],
            hasEnoughForShowcase: data.hasEnoughForShowcase || false
        };
    } catch (error) {
        console.error('[fetchShowcaseListings] Error:', error);
        return { fullShowcases: [], partialShowcases: [], hasEnoughForShowcase: false };
    }
}

/**
 * A row as `pickDataForListing` reads it: the fields SearchListingSchema
 * describes, plus `interest_count`, which the legacy API sends and the schema
 * does not mention.
 */
export interface PickableListingRow {
    interest_count?: number;
    distance?: number;
    text?: string | null;
    user?: { id?: string | number; firstName?: string; lastName?: string; trustLevel?: number } | null;
    id?: string | number;
    dataSourceId?: number | null;
    remote_url?: string | null;
    title?: string;
    price?: number | string | null;
    status?: number;
    photos?: unknown[];
    categoryId?: number;
    categoryGroupId?: number;
    locationText?: string | null;
    itemCondition?: number | null;
    slashedPrice?: string | null;
    percentOff?: number | null;
    original_price?: number | null;
    cost?: number | string | null;
    createdDate?: string | null;
    featuredDate?: string | null;
    tags?: string[];
}

export const pickDataForListing = (
    list: PickableListingRow[],
    max: number = 999999,
): Record<string, unknown>[] => {
    if (!list) return []
    return list.map((item, index) => {
        const {
            interest_count = 0,
            distance = 101,
            text,
            user,
            id,
            dataSourceId,
            remote_url = '',
            title,
            price,
            status,
            photos,
            categoryId,
            categoryGroupId,
            locationText = null,
            itemCondition,
            slashedPrice,
            percentOff,
            original_price,
            cost,
            createdDate,
            featuredDate,
            tags
        } = item;

        if (index > max) return;
        // Skip listings without user data (shouldn't happen with ES API)
        if (!user) {
            trackError(`[pickDataForListing] Listing ${id} has no user data`, ErrorSeverity.WARNING);
            return;
        }
        // Build base result object
        const result: Record<string, unknown> = {
            dataSourceId,
            title,
            price: price ? Number(price) : price,
            interest_count,
            categoryId,
            categoryGroupId,
            remote_url,
            // text,
            user: {
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                trustLevel: user.trustLevel,
            },
            id,
            status,
            photos,
            distance,
            locationText,
            itemCondition,
            createdDate,
            featuredDate: featuredDate || null,
            tags: tags || []
        };

        // Only add cost if it exists
        if (cost != null) {
            result.cost = typeof cost === 'number' ? cost : Number(cost);
        }

        // Only add discount fields if they're actually present and valid (for BargainHunter listings)
        if (slashedPrice && typeof slashedPrice === 'string') {
            result.slashedPrice = slashedPrice;
        }
        if (percentOff && typeof percentOff === 'number') {
            result.percentOff = percentOff;
        }
        if (original_price != null && typeof original_price === 'number') {
            result.original_price = original_price;
        }

        return result;
    }).filter((a): a is Record<string, unknown> => !!a);
};


/**
 * The filter options and their counts for a scope (a category, a group, or
 * everything) under the filters already chosen: /api/search/facets. Null when
 * the request fails or comes back in another shape, so the drawer shows the
 * standard filters alone rather than nothing.
 */
export async function fetchSearchFacets(
    scope: { categoryId?: number; categoryGroupId?: number },
    filters: FilterState,
): Promise<SearchFacets | null> {
    const params = new URLSearchParams(filtersToQuery(filters));
    if (scope.categoryId && scope.categoryId > 0) params.set('categoryId', String(scope.categoryId));
    else if (scope.categoryGroupId && scope.categoryGroupId > 0) params.set('categoryGroupId', String(scope.categoryGroupId));
    try {
        const response = await fetch(`/api/search/facets?${params.toString()}`);
        if (!response.ok) return null;
        const parsed = SearchFacetsResponseSchema.safeParse(await response.json());
        if (!parsed.success) return null;
        const { success: _success, ...facets } = parsed.data;
        return facets;
    } catch (error) {
        void trackError(error instanceof Error ? error : new Error('facets request failed'), ErrorSeverity.WARNING, { action: 'fetchSearchFacets' });
        return null;
    }
}
